import { TypeChecker } from '@attain-apps/type-guards'

/**
 * DsStyleOverrides represents interface with properties for textarea styles
 * that can be passed as inputs in text-area component
 */
export interface AtnDsStyleOverrides {
  /**
   * The resize CSS property sets whether an element is resizable, and if so, in
   * which directions.
   *
   * 'none' - Default value. The user cannot resize the element
   * 'both' - The user can resize both the height and width of the element
   * 'horizontal' - The user can resize the width of the element
   * 'vertical' - The user can resize the height of the element
   * 'initial' - Sets this property to its default value
   * 'inherit' - Inherits this property from its parent element
   */
  resize?: ResizeStyle
  backgroundColor?: string
  /**
   * Height property is string because of ability to pass px, percent, vh or other value type.
   * eg. height: '100px'
   *     height: '50%',
   *     height: '100vh'
   */
  height?: string
}

type ResizeStyle =
  | 'none'
  | 'both'
  | 'horizontal'
  | 'vertical'
  | 'initial'
  | 'inherit'

namespace ResizeStyle {
  export const isResize: TypeChecker<ResizeStyle> = TypeChecker.Factory.union(
    TypeChecker.Factory.exact('none'),
    TypeChecker.Factory.exact('both'),
    TypeChecker.Factory.exact('horizontal'),
    TypeChecker.Factory.exact('vertical'),
    TypeChecker.Factory.exact('initial'),
    TypeChecker.Factory.exact('inherit'),
  )
}

export namespace AtnDsStyleOverrides {
  export const isStyleOverrides =
    TypeChecker.Factory.object<AtnDsStyleOverrides>({
      backgroundColor: TypeChecker.Factory.optional(TypeChecker.isString),
      height: TypeChecker.Factory.optional(TypeChecker.isString),
      resize: TypeChecker.Factory.optional(ResizeStyle.isResize),
    })

  /**
   * Returns default style overrides for textarea if there is no input value
   * passed
   */
  export const DEFAULT: AtnDsStyleOverrides = {
    backgroundColor: 'inherit',
    height: '180px',
    resize: 'both',
  }
}
